/**
 * Validation for agent-mediated enrichment (0.2.0): the controlled vocabulary,
 * the domain/range table, id-prefix typing, and the request builder. Pure — no
 * I/O, no store; the caller passes a resolver so this stays testable.
 */

import type { RelationType } from "../types.js";
import { makeRelId } from "../sqlite/store.js";
import { extractDeterministic } from "../extract/deterministic.js";
import type { ExtractResult, RecordInput } from "../extract/types.js";
import { OBJECT_TYPES, RELATION_TYPES, DOMAIN_RANGE, typeFromId, domainRangeViolated } from "./domain-range.js";
import type { EnrichmentRequest, RawProposal, RejectReason, ValidatedProposal } from "./types.js";

// Re-exported so existing importers (barrel, tests) keep their paths.
export { OBJECT_TYPES, RELATION_TYPES, DOMAIN_RANGE, typeFromId } from "./domain-range.js";

function isRelationType(s: string): s is RelationType {
  return (RELATION_TYPES as readonly string[]).includes(s);
}

/** A resolver maps a proposed ref to a canonical object id, or null if unknown. */
export type Resolver = (ref: string) => string | null;

/**
 * Validate one raw proposal against the vocabulary, domain/range, and id
 * resolution order. Returns the validated proposal or a reject reason. Domain/range
 * is enforced only when BOTH ends resolve to a typed id; an unresolved end keeps its
 * raw label (sourceResolved/targetResolved=false) and skips the constraint it can't
 * check (a later resolution/confirmation re-checks it).
 */
export function validateProposal(
  raw: RawProposal,
  resolve: Resolver,
): { ok: true; value: ValidatedProposal } | { ok: false; reason: RejectReason } {
  if (!isRelationType(raw.rel)) return { ok: false, reason: "unknown-relation" };
  if (!(typeof raw.confidence === "number" && raw.confidence >= 0 && raw.confidence <= 1)) {
    return { ok: false, reason: "bad-confidence" };
  }
  if (!raw.evidencePath || typeof raw.evidencePath !== "string") {
    return { ok: false, reason: "no-evidence" };
  }
  // The verdict is agent-supplied JSON — validate it at runtime, not just at
  // compile time. A string "false" or a bogus verifier must NOT slip through as a
  // truthy/self verdict (Codex review 2026-06-18).
  if (typeof raw.agreed !== "boolean" || (raw.verifier !== "cross-model" && raw.verifier !== "self")) {
    return { ok: false, reason: "bad-verdict" };
  }

  const srcResolved = resolve(raw.sourceRef);
  const tgtResolved = resolve(raw.targetRef);
  let sourceId = srcResolved ?? raw.sourceRef;
  let targetId = tgtResolved ?? raw.targetRef;
  let sourceResolved = srcResolved !== null;
  let targetResolved = tgtResolved !== null;

  // Domain/range: only when a side's type is known (resolved to a typed id at
  // propose time — the confirm path re-checks against the objects' stored types).
  if (domainRangeViolated(raw.rel, typeFromId(sourceId), typeFromId(targetId))) {
    return { ok: false, reason: "domain-range" };
  }

  // Symmetric relations (contradicts) have no direction: canonicalize the endpoint
  // order so "A contradicts B" and "B contradicts A" share one relId — idempotent,
  // and a reverse-order deterministic edge is still caught as a duplicate (Codex).
  if (DOMAIN_RANGE[raw.rel].symmetric && targetId < sourceId) {
    [sourceId, targetId] = [targetId, sourceId];
    [sourceResolved, targetResolved] = [targetResolved, sourceResolved];
  }

  return {
    ok: true,
    value: {
      relId: makeRelId(sourceId, raw.rel, targetId),
      rel: raw.rel,
      sourceId,
      targetId,
      sourceResolved,
      targetResolved,
      confidence: raw.confidence,
      evidenceSpan: raw.evidenceSpan ?? null,
      evidencePath: raw.evidencePath,
    },
  };
}

const REQUEST_RULES: readonly string[] = [
  "Propose ONLY relations from the listed relation types — never invent a new type.",
  "Obey the domain/range table (an id's type is its prefix before ':').",
  "Reuse an id from alreadyFound.objectIds when it names the same entity; for anything else use a short, stable, natural label — do NOT guess canonical ids, the host merges duplicates locally.",
  "Cite an evidence span (a quote or char range) from THIS record for every proposal.",
  "A bare [[wikilink]] is a candidate only — do not emit it as a typed relation unless the prose states the relation.",
  "Skip relations deterministic extraction already found (listed in alreadyFound).",
  "Assign a confidence in [0,1]; omit anything you are not reasonably sure of.",
];

/**
 * Build the extraction request for one record (Phase 1 §4 step 3). Pure.
 * Extract-then-merge: no known-object catalog rides along — `alreadyFound`
 * (this record's own deterministic results) is the only id list, and candidate
 * labels are merged locally at ingest (see resolve.ts).
 */
export function buildEnrichmentRequest(record: RecordInput, deterministic: ExtractResult): EnrichmentRequest {
  return {
    path: record.path,
    category: record.category,
    prose: record.body,
    alreadyFound: {
      objectIds: deterministic.objects.map((o) => o.id),
      relations: deterministic.relations.map((r) => `${r.sourceId} ${r.rel} ${r.targetId}`),
    },
    vocabulary: {
      objectTypes: OBJECT_TYPES,
      relationTypes: RELATION_TYPES,
      domainRange: DOMAIN_RANGE,
    },
    rules: REQUEST_RULES,
  };
}

/** Convenience: deterministic extraction + the request that fills its gaps. */
export function requestFor(record: RecordInput): EnrichmentRequest {
  return buildEnrichmentRequest(record, extractDeterministic(record));
}
